<template>
  <div
    class="fixed left-0 top-0 bg-overlay w-full h-full z-65001"
  >
    <div
      class="bg-white fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg p-4 flex flex-col"
      :class="{
        'modal-content': editable
      }"
    >
      <div
        v-if="editable"
        ref="refScrollingContainer"
        class="flex-grow overflow-y-auto pb-4 relative flex flex-row"
      >
        <div
          v-if="loaded"
          class="flex flex-col gap-2"
        >
          <WonderListItem
            v-for="(w, index) in allWonders"
            :key="w.id"
            :active-wonder-count="activeWonderCounts['t' + w.ownerCapital]"
            :config="config[w.id]"
            :last="index >= allWonders.length - 4"
            :wonder="w"
            @activate="toggleWonderActive"
            @hover="toggleHover"
            @level="changeLevel"
          />
        </div>
        <div
          v-if="editable"
          class="relative h-full flex-grow"
        >
          <div
            ref="refEffectDescription"
            class="absolute left-0 w-full top-0 anim-t px-4 flex flex-col gap-2"
          >
            <template
              v-if="hoveredWonder"
            >
              <div class="font-medium">
                Effect{{ wonderMap[hoveredWonder].effects.count() > 1 ? 's' : '' }}
              </div>
              <div
                v-for="(_, index) in effectIndexes"
                :key="index"
                class="flex flex-row flex-wrap items-center"
              >
                {{ wonderMap[hoveredWonder].effects.getDescription(index, null, config[hoveredWonder].level) }}
              </div>
              <template
                v-if="wonderMap[hoveredWonder].synergies.length > 0"
              >
                <div class="font-medium">
                  Bonus{{ wonderMap[hoveredWonder].synergies.length > 1 ? 'es' : '' }}
                </div>
                <div
                  v-for="(synergy, index2) in wonderMap[hoveredWonder].synergies"
                  :key="index2"
                >
                  {{ synergy.effect.formatDescription(config[hoveredWonder].level) }}
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
      <div
        v-else
        class="flex flex-row flex-wrap pb-4"
      >
        <SelectedWonder
          v-for="w in allWonders"
          :key="w.id"
          :wonder="w"
        />
      </div>
      <div
        class="flex flex-row"
        :class="{
          'border-t border-roc-gray pt-4 w-full': editable
        }"
      >
        <template v-if="editable">
          <button
            class="rounded bg-blue-roc hover:bg-gray-600 transition duration-300 text-white uppercase text-base py-2 px-8 cursor-pointer mr-auto"
            @click="toggleMaxLevel"
          >
            Toggle Max
          </button>
          <button
            class="rounded bg-red-600 hover:bg-gray-600 transition duration-300 text-white uppercase text-base py-2 px-8 cursor-pointer"
            @click="cancel"
          >
            Cancel
          </button>
          <button
            class="ml-4 rounded bg-gray-roc hover:bg-gray-600 transition duration-300 text-white uppercase text-base py-2 px-8 cursor-pointer"
            @click="save"
          >
            Save
          </button>
        </template>
        <button
          v-else
          class="rounded bg-red-600 hover:bg-gray-600 transition duration-300 text-white uppercase text-base py-2 px-8 cursor-pointer ml-auto"
          @click="cancel"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Wonders from "@/roc/wonders";
import Eras from "@/roc/era";
import WonderOwner from "@/roc/wonders/base/WonderOwner";
import WonderListItem from "@/components/Wonders/WonderListItem.vue";
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {useWonderStore} from '@/roc/stores/WonderStore';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import SelectedWonder from '@/components/Wonders/SelectedWonder.vue';

export default {
  components: {
    SelectedWonder,
    WonderListItem,
  },
  emits: ['done', 'close'],
  setup () {
    const wonderStore = useWonderStore();
    const appStore = useApplicationStore();
    
    const config = ref({});
    const hoveredWonder = ref(null);
    const effectIndexes = ref(null);
    const loaded = ref(false);
    let topUpdateThread = null;

    const refScrollingContainer = ref(null);
    const refEffectDescription = ref(null);

    onBeforeUnmount(() => {
      if (topUpdateThread) {
        clearInterval(topUpdateThread);
        topUpdateThread = null;
      }
    });

    onMounted(() => {
      for (const i in wonderStore.wonders) {
        config.value[i] = {
          ...wonderStore.wonders[i]
        };
      }

      topUpdateThread = setInterval(() => {
        if (refScrollingContainer.value && refEffectDescription.value) {
          refEffectDescription.value.style.top = refScrollingContainer.value.scrollTop + 'px';
        }
      }, 100);

      loaded.value = true;
    });

    return {
      config,
      hoveredWonder,
      effectIndexes,
      loaded,

      refScrollingContainer,
      refEffectDescription,

      editable: computed(() => {
        return appStore.editable;
      }),

      wonderMap: computed(() => {
        return wonderStore.wonderMap;
      }),

      allWonders: computed(() => {
        const r = [];
        const currentEraIndex = Object.keys(Eras).indexOf(appStore.selectedEra);

        for (let i = 0; i < Wonders.length; i++) {
          if (appStore.editable || (config.value[Wonders[i].id] && config.value[Wonders[i].id].active)) {
            const wonderEraIndex = Object.keys(Eras).indexOf(Wonders[i].era);

            if (currentEraIndex >= wonderEraIndex) {
              r.push({
                id: Wonders[i].id,
                name: Wonders[i].name,
                icon: Wonders[i].icon + '.png',
                type: Wonders[i].type,
                owner: Wonders[i].owner,
                ownerCapital: WonderOwner.isAlliedCapital(Wonders[i].owner) ? WonderOwner.ALLIED_CAPITAL : WonderOwner.CAPITAL,
                isChina: Wonders[i].owner === WonderOwner.CHINA,
                isEgypt: Wonders[i].owner === WonderOwner.EGYPT,
                isMaya: Wonders[i].owner === WonderOwner.MAYA,
                isVikings: Wonders[i].owner === WonderOwner.VIKINGS,
                isArabia: Wonders[i].owner === WonderOwner.ARABIA,
                kind: Wonders[i].kind,
                kindMultiplier: Wonders[i].kindMultiplier
              });
            }
          }
        }

        return r;
      }),

      activeWonderCounts: computed(() => {
        const r = {};

        r['t' + WonderOwner.CAPITAL] = 0;
        r['t' + WonderOwner.ALLIED_CAPITAL] = 0;

        for (const i in config.value) {
          if (config.value[i].active) {
            if (WonderOwner.isAlliedCapital(wonderStore.wonderMap[config.value[i].id].owner)) {
              r['t' + WonderOwner.ALLIED_CAPITAL]++;
            } else {
              r['t' + WonderOwner.CAPITAL]++;
            }
          }
        }

        return r;
      }),

      save() {
        wonderStore.setWonderConfiguration(config.value);
        wonderStore.closeWonderConfiguration();
      },
      cancel() {
        wonderStore.closeWonderConfiguration();
      },
      changeLevel(level, id) {
        config.value[id].level = level;
      },
      toggleWonderActive(id) {
        config.value[id].active = !config.value[id].active;
      },
      toggleHover(id) {
        effectIndexes.value = id ? Array(wonderStore.wonderMap[id].effects.count()) : null;
        hoveredWonder.value = id;
      },
      toggleMaxLevel() {
        for (const i in config.value) {
          config.value[i].level = config.value[i].level === 30 ? 1 : 30;
        }
      }
    };
  }
};
</script>

<style scoped>
.modal-content {
  width: 800px;
  height: 700px;
}

.anim-t {
  transition: top 100ms ease-in-out;
}
</style>
